/**
 * CEPI - the shell's one chat panel.
 *
 * Every message is sent from exactly ONE source (spec 9.2): the thread's
 * attachment, the whole label corpus (pinned, or nothing else applies), or the
 * focused tab's published document. `effectiveSource` decides; the footer's
 * scope card always names it, or says it is not yet known. Only an explicit
 * user action creates or replaces the attachment: this panel offers the
 * focused tab's run ("Answer from these N results") and never attaches by
 * itself. Attach and detach are serialized (`scopeBusy`) and dropped if New
 * chat happened meanwhile.
 *
 * Supports all chat modes: drug_qa, drug_compare, search_results,
 * search_results_letters, regulatory_document (SBA + EPAR via Family
 * dispatch), fda_letter. Mode-specific behavior: title, examples, drug chips.
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { ChunkClickData } from '../types/qa';
import { StreamingMessage } from './StreamingMessage';
import { ScopeCard } from './chat/ScopeCard';
import type { PublishedContext } from '../shell/types';
import { useShell } from '../shell/shellContext';
import { capabilityPath, runTabPath, parseRunTabPath } from '../shell/tabState';
import type { RunResult } from '../shell/runResults';
import {
  effectiveSource, offerButtonLabel, isDifferentRun,
  attachmentKindName, attachmentTitle,
} from '../shell/cepiScope';
import { useActiveResult } from '../hooks/useActiveResult';
import { useJobWatcher } from '../contexts/JobWatcherContext';
import { captureResultSet } from '../services/captureResultSet';
import { startCohortJob, DISCOVERY_MAX_RESULTS } from '../services/startCohortJob';
import { attachResult, detachActiveResult, cancelResearchJob } from '../services/activeResult';
import { capabilityRunPhase, type JobOutcome } from '../services/watchedJobs';
import type { CapabilityAction } from '../types/qa';
import { MessageBubble } from './chat';
import { SupportReportCard } from './chat/SupportReportCard';
import { isSupportRequest } from '../services/supportReport';
import GenerateDeckButton from './GenerateDeckButton';
import { useUnifiedChat } from '../hooks/useUnifiedChat';
import { useOriginScope } from '../hooks/useOriginScope';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { isUsFdaVisible } from '../constants/filterConstants';
import type { ChatMode, UnifiedChatContext } from '../hooks/useUnifiedChat';

// =============================================================================
// Mode-specific config
// =============================================================================

interface ModeConfig {
  title: string;
  tagline: string;
  accentColor: string;
  hoverColor: string;
  examples: string[];
  placeholder: string;
}

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  drug_qa: {
    title: 'CEPI',
    tagline: 'Get answers from the drug label.',
    accentColor: 'indigo',
    hoverColor: 'hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700',
    examples: [
      'What are the contraindications?',
      'What are the most common adverse reactions?',
      'Compare the warnings between these versions',
      'What changed in the dosing section?',
    ],
    placeholder: 'Ask about this label...',
  },
  drug_compare: {
    title: 'CEPI',
    tagline: 'Compare drug labels side by side.',
    accentColor: 'purple',
    hoverColor: 'hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700',
    examples: [
      'Compare the warnings for these drugs',
      'Which drug has more indications?',
      'Do they share any contraindications?',
      'Compare the dosing regimens',
    ],
    placeholder: 'Ask to compare these drugs...',
  },
  search_results: {
    title: 'CEPI',
    tagline: 'Ask questions about your search results.',
    accentColor: 'emerald',
    hoverColor: 'hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700',
    examples: [
      'Which of these are oncology drugs?',
      'Compare the boxed warnings',
      'Which were approved after 2020?',
      'Which drugs have pediatric data?',
    ],
    placeholder: 'Ask about these results...',
  },
  // FDA-letter corpus variant of the search-results chat. Same conceptual
  // mode (chat scoped to the current search response) but the context is
  // a letter list, not a drug list — backend mode discriminator gates the
  // tool selection (`_search_fda_letters_corpus`).
  search_results_letters: {
    title: 'CEPI',
    tagline: 'Ask questions about your FDA Enforcement Letter search results.',
    accentColor: 'rose',
    hoverColor: 'hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700',
    examples: [
      'Which letters cite Boxed Warning violations?',
      'Summarize the common violations across these letters',
      'Which letters were issued after 2020?',
      'What corrective actions did FDA request most often?',
    ],
    placeholder: 'Ask about these FDA Enforcement Letters...',
  },
  regulatory_document: {
    title: 'CEPI',
    tagline: 'Ask questions about this regulatory review document.',
    accentColor: 'teal',
    hoverColor: 'hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700',
    examples: [
      'What was the overall benefit-risk assessment?',
      'Summarize the primary efficacy endpoints and results',
      'What safety concerns were noted by the reviewers?',
      'What was the recommended indication?',
      'Who were the reviewers on this application?',
    ],
    placeholder: 'Ask about this regulatory review...',
  },
  fda_letter: {
    title: 'CEPI',
    tagline: 'Ask questions about this FDA Enforcement Letter.',
    accentColor: 'rose',
    hoverColor: 'hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700',
    examples: [
      'What violations did FDA identify in this letter?',
      'What corrective actions were requested?',
      'What promotional claims were found misleading?',
      'Summarize the key regulatory concerns.',
    ],
    placeholder: 'Ask about this FDA Enforcement Letter...',
  },
};

/** The header's second line: the same on every page. */
const PANEL_SUBTITLE = 'What should we look up?';

/** What the request scope is when no page publishes a context (Home, drafts, ...). */
const SHELL_DEFAULT_CONFIG: ModeConfig = {
  title: 'CEPI',
  tagline: 'Ask across the whole label corpus.',
  accentColor: 'indigo',
  hoverColor: MODE_CONFIGS.drug_qa.hoverColor,
  examples: [
    'Which labels carry a boxed warning for hepatotoxicity?',
    'Which products were approved after 2020 for lupus?',
  ],
  placeholder: 'Ask CEPI...',
};

export interface UnifiedChatPanelProps {
  published: PublishedContext | null;
  /** Shell-supplied control rendered at the end of the header (the collapse button). */
  headerAction?: React.ReactNode;
}

/** R3.4: the popover body for a just-launched run. */
function announceBody(scope: { member_drug_ids?: number[] | null }): string {
  const n = (scope.member_drug_ids ?? []).length;
  const head = n > 0
    ? `Checking each of the ${n} products.`
    : `Searching labels; stops at ${DISCOVERY_MAX_RESULTS} matches.`;
  return `${head} You can stay here; the tab shows its progress and the result count when it's done.`;
}

type StopState = 'stopping' | 'cancelled' | 'ended' | 'error';

const UnifiedChatPanel: React.FC<UnifiedChatPanelProps> = ({ published, headerAction }) => {
  const { runs, activeTabId, tabs, openTab, openCitation, announceTab } = useShell();
  const [activeResultToken, setActiveResultToken] = useState(0);

  // The source depends on state the chat hook owns (threadId, corpusPinned), so
  // it cannot be an option of the hook. The hook reads these refs at send time;
  // a layout effect (committed state only, before paint and before any event
  // can reach the composer) keeps them current. No render-time ref write.
  const sourceContextRef = useRef<UnifiedChatContext | null>(null);
  const versionScopeRef = useRef<'origin' | undefined>(undefined);
  const isAdmin = useIsAdmin();
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;
  const {
    messages, isLoading, isStreaming, streamingContent, streamingStatus,
    streamingCitations, streamingToolUse, askQuestion, cancelStream,
    threadId, setThreadId, trimmed, newChat, retry, markOpened,
    corpusPinned, setCorpusPinned,
  } = useUnifiedChat({
    getContext: () => sourceContextRef.current,
    getVersionScope: () => versionScopeRef.current,
    getDrugsAtFda: () => isUsFdaVisible(isAdminRef.current),
  });
  const { fetch: scopeFetch, retry: retryScope } = useActiveResult(threadId, activeResultToken);
  const source = effectiveSource(scopeFetch, corpusPinned, published);
  const checking = source.kind === 'checking';
  const scopeUnknown = checking || source.kind === 'error';
  const attachmentResult = scopeFetch.status === 'loaded' ? scopeFetch.result : null;
  const sourceContext = source.context;
  const config = source.kind === 'document' && sourceContext
    ? MODE_CONFIGS[sourceContext.mode]
    : source.kind === 'attachment' || source.kind === 'unavailable'
      ? MODE_CONFIGS.search_results
      : SHELL_DEFAULT_CONFIG;
  const defaultRegulatoryFamily = sourceContext?.mode === 'regulatory_document'
    ? sourceContext.regulatory_document.family
    : undefined;
  // The origin toggle scopes label tools to a document; it is meaningless for
  // an attachment or the corpus. `checking` keeps it so a reload on a label
  // tab does not clear ?is_origin while the attachment is being looked up.
  const originEligible = (published?.originEligible ?? false)
    && (source.kind === 'document' || checking);
  const originScope = useOriginScope(originEligible);
  useLayoutEffect(() => {
    sourceContextRef.current = sourceContext;
    versionScopeRef.current = originScope.isOrigin ? 'origin' : undefined;
  });
  const longRunningMessage =
    streamingToolUse?.longRunning && streamingToolUse.userMessage
      ? streamingToolUse.userMessage
      : null;

  const [inputValue, setInputValue] = useState('');
  // Support report draft: shown instead of asking CEPI when the whole message
  // is a "help / report a problem" phrase. Never sent without its Submit click.
  const [supportDraft, setSupportDraft] = useState<{
    key: number; query: string; searchQuery: string; pageLabel: string; page: string;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, supportDraft]);

  const [scopeBusy, setScopeBusy] = useState(false);
  // Scope not yet settled: a pending lookup or an attach/detach in flight. A
  // send now could pair the old context with the new thread (I2). `error` is
  // deliberately NOT blocking (T9b).
  const sendBlocked = checking || scopeBusy;
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading || sendBlocked) return;
    const question = inputValue.trim();
    setInputValue('');
    if (isSupportRequest(question)) {
      const previous = [...messages].reverse().find((m) => m.role === 'user');
      const tab = tabs.find((t) => t.id === activeTabId);
      const run = activeTabId ? runs[activeTabId] : undefined;
      const searchQuery = run?.kind === 'set' ? (run.searchQuery ?? run.queryText) : (run?.queryText ?? '');
      setSupportDraft({
        key: Date.now(),
        query: (previous?.content ?? '').slice(0, 5000),
        searchQuery: searchQuery.slice(0, 5000),
        pageLabel: tab?.title ?? '',
        page: tab ? `${tab.title} (${tab.path})`.slice(0, 4000) : '',
      });
      return;
    }
    setSupportDraft(null);
    await askQuestion(question);
  }, [inputValue, isLoading, sendBlocked, askQuestion, messages, tabs, activeTabId, runs]);
  // Retry re-sends with the current context, so it obeys the same gate.
  const handleRetry = useCallback((messageId: string) => {
    if (sendBlocked) return;
    retry(messageId);
  }, [sendBlocked, retry]);

  // ---- citations: ONE route, the shell resolves the destination tab's handler ----
  // `document_type: 'label'` is what lets a section link carrying a drug id
  // route to that drug's tab; the second argument picks the destination page's
  // own section-link / chunk handler.
  const onSectionLinkClick = useCallback((sectionNumber: string, drugId?: number) => {
    openCitation({
      section_number: sectionNumber,
      document_type: 'label',
      ...(drugId != null ? { drug_id: drugId } : {}),
    }, 'section');
  }, [openCitation]);
  const onChunkClick = useCallback((c: ChunkClickData) => {
    openCitation({
      section_number: c.sectionNumber,
      document_type: 'label',
      ...(c.chunkId != null ? { chunk_id: c.chunkId } : {}),
      anchor_start: c.anchorStart,
      anchor_end: c.anchorEnd,
      anchor_length: c.anchorLength,
    }, 'chunk');
  }, [openCitation]);

  // ---- capability recommendations ----
  const openAction = useCallback((action: CapabilityAction) => {
    const path = capabilityPath(action);
    if (path) openTab(path);
  }, [openTab]);
  const openAgents = useCallback(() => openTab('/?mode=workstream'), [openTab]);

  // D14: Cogni and Keyword open their tab by themselves, ONCE per message. The
  // mark lives on the message (persisted), and messages that were already here
  // at mount (restored from storage) are never candidates, so a reload cannot
  // re-open anything.
  // (An identity change remounts the whole shell, chat included - T8 keys
  // ShellProvider by identity - so a restore after one is not handled here.)
  const restoredIds = useRef<Set<string> | null>(null);
  if (restoredIds.current === null) restoredIds.current = new Set(messages.map((m) => m.id));
  const handledIds = useRef(new Set<string>());
  useEffect(() => {
    for (const m of messages) {
      const a = m.capability_action;
      if (!a || m.isLoading || m.opened || handledIds.current.has(m.id) || restoredIds.current!.has(m.id)) continue;
      if (a.capability !== 'cogni' && a.capability !== 'keyword') continue;
      if (!a.options.includes('open')) continue;
      if (m.answer_status === 'evidence_incomplete' || m.answer_status === 'no_answer') continue;
      handledIds.current.add(m.id);
      markOpened(m.id);
      openAction(a);
    }
  }, [messages, markOpened, openAction]);

  // "Run it for me": guarded by a ref, so two clicks in one
  // tick cannot both launch. The app-level watcher only reports completion;
  // attaching is the user's choice.
  const jobWatcher = useJobWatcher();
  const launchedRuns = useRef<Map<string, string | null>>(new Map());
  const [launchedJobs, setLaunchedJobs] = useState<Map<string, string | null>>(new Map());
  // Every outcome of a launched run, copied out of the watcher so the card
  // survives the toast being dismissed (dismiss removes the entry from `finished`).
  const [outcomes, setOutcomes] = useState<Map<string, { outcome: JobOutcome; matched: number }>>(new Map());
  const finishedJobs = jobWatcher?.finished;
  useEffect(() => {
    if (!finishedJobs?.length) return;
    const launched = new Set(launchedJobs.values());
    const fresh = finishedJobs.filter((f) => launched.has(f.jobId));
    if (!fresh.length) return;
    setOutcomes((prev) => {
      if (fresh.every((f) => {
        const p = prev.get(f.jobId);
        return p?.outcome === f.outcome && p.matched === (f.matched ?? 0);
      })) return prev;
      const next = new Map(prev);
      for (const f of fresh) next.set(f.jobId, { outcome: f.outcome, matched: f.matched ?? 0 });
      return next;
    });
  }, [finishedJobs, launchedJobs]);
  const [stops, setStops] = useState<Map<string, StopState>>(new Map());
  const stopsRef = useRef<Map<string, StopState>>(new Map());
  const handleStop = useCallback(async (jobId: string) => {
    const cur = stopsRef.current.get(jobId);
    if (cur === 'stopping' || cur === 'cancelled' || cur === 'ended') return;
    const set = (v: StopState) => {
      stopsRef.current.set(jobId, v);
      setStops(new Map(stopsRef.current));
    };
    set('stopping');
    set(await cancelResearchJob(jobId));
  }, []);
  const [startError, setStartError] = useState<string | null>(null);
  const handleCapabilityRun = useCallback((action: CapabilityAction, messageId: string) => {
    if (launchedRuns.current.has(messageId)) return;
    launchedRuns.current.set(messageId, null);
    setLaunchedJobs(new Map(launchedRuns.current));
    void (async () => {
      setStartError(null);
      const { jobId, error } = await startCohortJob(action.scope_draft);
      if (error || !jobId) {
        launchedRuns.current.delete(messageId);
        setLaunchedJobs(new Map(launchedRuns.current));
        if (error) setStartError(error);
        return;
      }
      launchedRuns.current.set(messageId, jobId);
      setLaunchedJobs(new Map(launchedRuns.current));
      const criterion = action.scope_draft.criterion || '';
      jobWatcher?.watch({ jobId, threadId, criterion });
      const path = runTabPath(jobId, action.scope_draft);
      openTab(path, { background: true });
      announceTab(path, announceBody(action.scope_draft));
    })();
  }, [jobWatcher, threadId, openTab, announceTab]);

  // ---- scope mutations: attach / detach, one at a time ----
  // `scopeBusy` disables every control that would start another mutation.
  // Each mutation captures a generation; New chat bumps it AND clears the busy
  // flag, so a superseded continuation neither applies its effects nor leaves
  // the controls disabled.
  const [attachError, setAttachError] = useState<string | null>(null);
  const [detachError, setDetachError] = useState(false);
  const [attachedRecord, setAttachedRecord] = useState<{ tabId: string; seq: number } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Runs whose switch prompt the user dismissed with Keep (panel memory only).
  const [keptSeqs, setKeptSeqs] = useState<Set<number>>(new Set());
  const scopeBusyRef = useRef(false);
  const scopeGenRef = useRef(0);
  const runScope = useCallback(async <T,>(
    fn: (isCurrent: () => boolean) => Promise<T>,
  ): Promise<T | undefined> => {
    if (scopeBusyRef.current) return undefined;
    scopeBusyRef.current = true;
    setScopeBusy(true);
    const gen = ++scopeGenRef.current;
    try {
      return await fn(() => gen === scopeGenRef.current);
    } finally {
      if (gen === scopeGenRef.current) {
        scopeBusyRef.current = false;
        setScopeBusy(false);
      }
    }
  }, []);

  // The run is a parameter (not read from the focused tab) so T8b's picker can
  // attach another tab's run without focusing it.
  const attachRun = useCallback((run: RunResult, tabId: string | null) => runScope(async (isCurrent) => {
    setAttachError(null);
    setDetachError(false);
    const out = run.kind === 'job'
      // Investigator's attach is a POINTER; Cogni and Keyword are a SNAPSHOT.
      ? await attachResult(run.jobId, { threadId, mode: 'user' })
      : await captureResultSet({
        capability: run.capability,
        ...(run.corpus ? { corpus: run.corpus } : {}),
        queryText: run.queryText,
        request: run.request,
        response: run.response,
        threadId,
      });
    if (!isCurrent()) return;
    // The two calls report failure differently; narrow on the run kind.
    const problem = run.kind === 'job'
      ? ('attached' in out && out.attached ? null : (('reason' in out && out.reason) || 'it could not be attached'))
      : ('error' in out ? out.error ?? null : null);
    if (problem) {
      setAttachError(problem);
      return;
    }
    setAttachedRecord(run.kind === 'set' && tabId ? { tabId, seq: run.seq } : null);
    setCorpusPinned(false);
    // The server may have MINTED the thread on this call; prefer its id.
    const newThreadId = out.threadId ?? threadId ?? null;
    if (newThreadId) setThreadId(newThreadId);
    setActiveResultToken((n) => n + 1);
  }), [runScope, threadId, setThreadId, setCorpusPinned]);

  // Resolves true when nothing is attached any more (including "nothing was").
  // The server answers false for "nothing attached", so no POST unless the
  // fetched state says there is something to remove.
  const detachInner = useCallback(async (isCurrent: () => boolean): Promise<boolean> => {
    setDetachError(false);
    if (!threadId || !attachmentResult || attachmentResult.state === 'missing') return true;
    const ok = await detachActiveResult(threadId);
    if (!isCurrent()) return false;
    if (!ok) {
      setDetachError(true);
      return false;
    }
    setAttachedRecord(null);
    setActiveResultToken((n) => n + 1);
    return true;
  }, [threadId, attachmentResult]);
  const detach = useCallback(
    async (): Promise<boolean> => (await runScope(detachInner)) ?? false,
    [runScope, detachInner],
  );

  // Picker rows 2 and 3: one serialized mutation (detach, then the pin), so a
  // New chat during the detach drops the pin instead of applying it late.
  const pickSource = useCallback((pin: boolean) => {
    setPickerOpen(false);
    void runScope(async (isCurrent) => {
      if (!(await detachInner(isCurrent))) return;
      if (!isCurrent()) return;
      setCorpusPinned(pin);
    });
  }, [runScope, detachInner, setCorpusPinned]);

  const handleNewChat = useCallback(() => {
    scopeGenRef.current += 1;
    scopeBusyRef.current = false;
    setScopeBusy(false);
    setAttachedRecord(null);
    setAttachError(null);
    setDetachError(false);
    setPickerOpen(false);
    setKeptSeqs(new Set());
    setSupportDraft(null);
    newChat();
  }, [newChat]);

  // The focused tab's run.
  const run = activeTabId ? runs[activeTabId] : undefined;
  const runKindName = run
    ? (run.kind === 'job' ? 'Investigator' : run.capability === 'keyword' ? 'Keyword' : 'Cogni')
    : '';
  const sourceTabTitle = (() => {
    if (!attachmentResult) return undefined;
    const tabId = attachedRecord?.tabId
      ?? Object.entries(runs).find(([, r]) => r.kind === 'job' && r.jobId === attachmentResult.job_id)?.[0];
    return tabId ? tabs.find((t) => t.id === tabId)?.title : undefined;
  })();
  const showSwitchPrompt = !!run && !!attachmentResult && !!activeTabId
    && source.kind === 'attachment' && !keptSeqs.has(run.seq)
    && isDifferentRun(run, attachmentResult, attachedRecord, activeTabId);
  const pickerTabs = tabs.filter((t) => runs[t.id]);
  const documentTitle = published?.exportTitle ?? config.tagline;
  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pickerOpen]);
  const renderedMessages = isStreaming
    ? messages.filter((m) => !(m.role === 'assistant' && m.isLoading))
    : messages;

  const originStrip = originScope.canUseOrigin && originEligible ? (
    <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-amber-600"
          checked={originScope.isOrigin}
          onChange={(e) => originScope.setIsOrigin(e.target.checked)}
        />
        <span className={originScope.isOrigin ? 'font-semibold text-amber-900' : 'text-slate-600'}>
          Answer from the ORIGINAL label
        </span>
        <span className="text-slate-400">(admin)</span>
      </label>
    </div>
  ) : null;

  const launchedSet = new Set(launchedJobs.values());
  const runningHere = !!jobWatcher?.running.some((j) => launchedSet.has(j.jobId));

  return (
    <div className="flex h-full min-h-0 flex-col bg-panel" data-testid="shell-chat">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 flex-shrink-0">
        <div className="min-w-0">
          <h3 className="font-display text-[17px] font-semibold text-ink">{config.title}</h3>
          <p className="text-xs text-slate-500 truncate">{PANEL_SUBTITLE}</p>
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          data-testid="shell-new-chat"
          className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
        >
          New chat
        </button>
        {headerAction}
      </div>
      {originStrip}
      <div className={`overflow-y-auto p-2 space-y-2 flex flex-col min-h-0 ${messages.length > 0 || isStreaming || supportDraft ? 'flex-1' : ''}`}>
        {trimmed && (
          <p className="text-center text-[11px] text-slate-400" data-testid="shell-trimmed-note">
            Earlier messages were cleared
          </p>
        )}
        {messages.length === 0 && !isStreaming ? (
          <div className="py-4 px-3">
            <p className="text-sm text-slate-600 text-center mb-4">{config.tagline}</p>
            <div className="space-y-2">
              {config.examples.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => { setInputValue(q); inputRef.current?.focus(); }}
                  className={`block w-full text-left px-3 py-2 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg ${config.hoverColor} transition-colors`}
                >
                  "{q}"
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {renderedMessages.map((message) => {
              const a = message.capability_action;
              const jobId = launchedJobs.get(message.id);
              const o = jobId ? outcomes.get(jobId) : undefined;
              const doneMatched = o?.outcome === 'complete' ? o.matched : undefined;
              const showDone = jobId && doneMatched !== undefined
                && attachmentResult?.job_id !== jobId;
              const resultsTab = jobId
                ? tabs.find((t) => parseRunTabPath(t.path)?.jobId === jobId)
                : undefined;
              const progress = jobId ? jobWatcher?.progress?.[jobId] ?? null : null;
              const stopState = jobId ? stops.get(jobId) : undefined;
              const phase = launchedJobs.has(message.id)
                ? capabilityRunPhase({ jobId: jobId ?? null, outcome: o?.outcome, stopped: stopState === 'cancelled', progress })
                : null;
              const scope = a?.scope_draft;
              const pinnedCount = (scope?.member_drug_ids ?? []).length;
              const runPath = jobId && scope ? runTabPath(jobId, scope) : null;
              const capabilityRun = phase && phase !== 'done' ? {
                phase, progress, pinned: pinnedCount > 0, pinnedCount,
                onGoToTab: runPath ? () => openTab(runPath) : undefined,
                onStop: jobId ? () => void handleStop(jobId) : undefined,
                stop: stopState === undefined || stopState === 'cancelled' ? 'idle' as const : stopState,
              } : undefined;
              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  streamingStatus={streamingStatus}
                  onCitationClick={openCitation}
                  onSectionLinkClick={onSectionLinkClick}
                  onChunkClick={onChunkClick}
                  defaultRegulatoryFamily={defaultRegulatoryFamily}
                  // D13: a pinned cohort is Run-only; opening discovery would drop the pin.
                  onCapabilityOpen={a && capabilityPath(a) === null ? undefined : openAction}
                  onCapabilityRun={handleCapabilityRun}
                  capabilityBusy={launchedJobs.has(message.id)}
                  capabilityRun={capabilityRun}
                  // Only while its tab exists: at the cap a Cancel opens nothing.
                  capabilityOpened={!!message.opened && !!a
                    && tabs.some((t) => t.path === capabilityPath(a))}
                  onCapabilityFocus={openAction}
                  capabilityDone={showDone && jobId ? {
                    matched: doneMatched,
                    onAnswer: () => void attachRun({
                      kind: 'job', jobId, count: doneMatched,
                      queryText: a?.scope_draft.criterion ?? '', seq: -1,
                    }, null),
                    ...(resultsTab ? { onOpenTab: () => openTab(resultsTab.path) } : {}),
                  } : undefined}
                  capabilityDoneBusy={scopeBusy}
                  onOpenAgents={openAgents}
                  onRetry={handleRetry}
                  onOpenPath={openTab}
                />
              );
            })}
            {isStreaming && (
              <StreamingMessage
                content={streamingContent}
                citations={streamingCitations}
                status={streamingStatus}
                isStreaming={isStreaming}
                longRunningMessage={longRunningMessage}
                onCitationClick={openCitation}
                onCancel={cancelStream}
              />
            )}
          </>
        )}
        {supportDraft && (
          <SupportReportCard
            key={supportDraft.key}
            query={supportDraft.query}
            searchQuery={supportDraft.searchQuery}
            pageLabel={supportDraft.pageLabel}
            page={supportDraft.page}
            conversationId={threadId}
            onCancel={() => setSupportDraft(null)}
          />
        )}
        {(messages.length > 0 || isStreaming || supportDraft) && <div ref={messagesEndRef} />}
      </div>

      <div className="flex-shrink-0">
        {(attachError || startError) && (
          <div className="mx-2 mb-1 text-[11px] text-amber-700" data-testid={attachError ? 'attach-error' : 'shell-start-error'}>
            {attachError ? `Those results could not be attached — ${attachError}` : startError}
          </div>
        )}
        {run && (source.kind === 'corpus' || source.kind === 'document') && (
          <div className="mx-2 mb-1 rounded border-2 border-accent bg-white px-2.5 py-1.5 text-[12px] text-ink" data-testid="cepi-offer">
            <div className="font-medium">
              This tab has {run.count} {runKindName} result{run.count === 1 ? '' : 's'}
            </div>
            {run.queryText && <div className="truncate text-ink-2">{run.queryText}</div>}
            <button
              type="button"
              onClick={() => void attachRun(run, activeTabId)}
              disabled={scopeBusy}
              className="mt-1 rounded bg-ink px-2 py-0.5 text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {offerButtonLabel(run)}
            </button>
          </div>
        )}
        <div className="mx-2 mb-1 flex items-center justify-between">
          <span className="eyebrow">CEPI IS ANSWERING FROM</span>
          <button
            type="button"
            data-testid="cepi-scope-change"
            onClick={() => setPickerOpen((o) => !o)}
            disabled={scopeUnknown}
            className="text-[12px] text-ink-2 underline underline-offset-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            Change
          </button>
        </div>
        {/* Hidden, not just Change-disabled, while the scope is unknown: a corpus pick
            there would skip the detach and pin over a possible attachment. */}
        {pickerOpen && !scopeUnknown && (
          <div className="mx-2 mb-1 rounded border border-line bg-white text-[12px] text-ink" data-testid="cepi-scope-picker">
            {pickerTabs.map((t) => {
              const r = runs[t.id]!;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={scopeBusy}
                  onClick={() => { setPickerOpen(false); void attachRun(r, t.id); }}
                  className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t.title} · {r.count} results
                </button>
              );
            })}
            <button
              type="button"
              data-testid="cepi-scope-corpus"
              disabled={scopeBusy}
              onClick={() => pickSource(true)}
              className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Whole label corpus
            </button>
            {published && (
              <button
                type="button"
                disabled={scopeBusy}
                onClick={() => pickSource(false)}
                className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                This tab: {documentTitle}
              </button>
            )}
          </div>
        )}
        {showSwitchPrompt && run && attachmentResult && (
          <div className="mx-2 mb-1 rounded border-2 border-warn-line bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn-ink" data-testid="cepi-switch-prompt">
            <div>
              You're on the {tabs.find((t) => t.id === activeTabId)?.title ?? 'current'} tab now.
              CEPI is still answering from {attachmentTitle(attachmentResult).text}. Which should it use?
            </div>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                disabled={scopeBusy}
                onClick={() => void attachRun(run, activeTabId)}
                className="rounded bg-ink px-2 py-0.5 text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                Switch to these {run.count} {runKindName} results
              </button>
              <button
                type="button"
                onClick={() => setKeptSeqs((k) => new Set(k).add(run.seq))}
                className="rounded border border-warn-line px-2 py-0.5 text-[11px]"
              >
                Keep {attachmentKindName(attachmentResult)}
              </button>
            </div>
          </div>
        )}
        <ScopeCard
          kind={source.kind}
          result={attachmentResult}
          sourceTabTitle={sourceTabTitle}
          documentTitle={documentTitle}
          busy={scopeBusy}
          detachError={detachError}
          onRetry={retryScope}
          onDetach={() => void detach()}
        />
      </div>

      <form onSubmit={handleSubmit} className="p-2 border-t border-slate-200 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              const el = e.target;
              el.style.height = 'auto';
              el.style.height = `${el.scrollHeight}px`;
            }}
            placeholder={runningHere ? 'Keep asking while it runs…' : config.placeholder}
            disabled={isLoading}
            rows={1}
            className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 resize-none overflow-hidden"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading || sendBlocked}
            aria-label="Send"
            className="px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </form>
      {published?.exportTitle && (
        <div className="px-2 pb-2 pt-0 flex items-center">
          <GenerateDeckButton
            title={published.exportTitle}
            drugNames={published.exportDrugNames ?? []}
            messages={messages.filter((m) => !m.isLoading).map((m) => ({ role: m.role, content: m.content }))}
          />
        </div>
      )}
    </div>
  );
};

export default UnifiedChatPanel;
